import { z } from "zod";
import { DateTimeWithOffset } from "@/lib/api/schemas/patterns";
import { Auth } from "../auth";
import type { ActionReturnType } from "../types";
import { request, validateInput } from "../request";
import { CasePayload } from "@/lib/api/schemas/case";
import { CaseAppResetMode } from "@/lib/api/schemas/suite";
import { AI_REVIEW_TIMEOUT_MS } from "@/config";

class CasesEndpoint {
  public static readonly basePath = "cases";
  public static readonly schemas = {
    list: {
      response: z.object({
        count: z.number(),
        items: z
          .object({
            id: z.number(),
            created_at: DateTimeWithOffset,
            name: z.string(),
            key: z.string(),
            payload: CasePayload,
            project_id: z.number(),
            artifact_id: z.number().nullable().optional(),
          })
          .array(),
      }),
    },
    create: {
      body: z.object({
        name: z.string(),
        // Left out on purpose: the backend mints the key from the project key and the
        // lowest free number (e.g. `viva-7`), so the UI never asks for one.
        key: z.string().optional(),
        payload: z.string(), // Raw string input for backend
        app_reset_mode: CaseAppResetMode.optional(),
      }),
      response: z.object({
        id: z.number(),
        created_at: DateTimeWithOffset,
        name: z.string(),
        key: z.string(),
        project_id: z.number(),
        artifact_id: z.number().nullable().optional(),
      }),
    },
    read: {
      response: z.object({
        id: z.number(),
        created_at: DateTimeWithOffset,
        name: z.string(),
        key: z.string(),
        payload: CasePayload,
        project_id: z.number(),
        artifact_id: z.number().nullable().optional(),
        suite_ids: z.number().array().nullable(),
        // Which version this row is, straight off the cheap read. `cases/detail/`
        // carries the same two fields, but it also drags in `case_runs` and can
        // take tens of seconds on a case with a long run history — the version
        // badge and the run/edit gating must not wait for that.
        version: z.number(),
        is_current: z.boolean(),
        root_version_id: z.number().nullable().optional(),
        previous_version_id: z.number().nullable().optional(),
        /** This case's own say on what happens to the app before it runs; `inherit` leaves it to the suite. */
        app_reset_mode: CaseAppResetMode,
      }),
    },
    update: {
      // A full PUT, and an unforgiving one: a body without `app_reset_mode` does
      // not leave the case's mode alone, it resets it to `inherit`. Required
      // here rather than optional so every caller has to carry it over.
      body: z.object({
        name: z.string(),
        key: z.string().optional(),
        payload: z.string(),
        app_reset_mode: CaseAppResetMode,
      }),
      response: z.object({
        id: z.number(),
        created_at: z.string(),
        name: z.string(),
        key: z.string(),
        project_id: z.number(),
        version: z.number(),
        is_current: z.boolean(),
        deleted_at: z.string().nullable().optional(),
        root_version_id: z.number().nullable().optional(),
        previous_version_id: z.number().nullable().optional(),
      }),
    },
    detail: {
      response: z.object({
        id: z.number(),
        created_at: z.string(),
        name: z.string(),
        key: z.string(),
        project_id: z.number(),
        version: z.number(),
        is_current: z.boolean(),
        deleted_at: z.string().nullable().optional(),
        root_version_id: z.number().nullable().optional(),
        previous_version_id: z.number().nullable().optional(),
        version_history: z
          .array(
            z.object({
              id: z.number(),
              created_at: z.string(),
              name: z.string(),
              key: z.string(),
              project_id: z.number(),
              version: z.number(),
              is_current: z.boolean(),
              deleted_at: z.string().nullable().optional(),
              root_version_id: z.number().nullable().optional(),
              previous_version_id: z.number().nullable().optional(),
            }),
          )
          .nullable()
          .optional(),
      }),
    },
    review: {
      body: z.object({
        text: z.string().trim().min(1),
      }),
      response: z.object({
        rewritten_test_case: z.string(),
        general_notes: z.string(),
        model: z.string(),
      }),
    },
  };

  constructor(private api: Auth) {}

  public list(): ActionReturnType<z.infer<typeof CasesEndpoint.schemas.list.response>> {
    return request({
      log: ["Cases", "list"],
      method: "get",
      path: [CasesEndpoint.basePath],
      response: CasesEndpoint.schemas.list.response,
    });
  }

  public async create(
    body: z.input<typeof CasesEndpoint.schemas.create.body>,
    query: { project_id: string },
  ): ActionReturnType<z.infer<typeof CasesEndpoint.schemas.create.response>> {
    const bodyValidated = validateInput(CasesEndpoint.schemas.create.body, body);
    if (!bodyValidated.ok) return bodyValidated;
    return request({
      log: ["Cases", "create"],
      method: "post",
      path: [CasesEndpoint.basePath],
      query,
      json: bodyValidated.data,
      response: CasesEndpoint.schemas.create.response,
    });
  }

  public read(id: number | string): ActionReturnType<z.infer<typeof CasesEndpoint.schemas.read.response>> {
    return request({
      log: ["Cases", "read"],
      method: "get",
      path: [CasesEndpoint.basePath, id],
      response: CasesEndpoint.schemas.read.response,
    });
  }

  public detail(id: number | string): ActionReturnType<z.infer<typeof CasesEndpoint.schemas.detail.response>> {
    return request({
      log: ["Cases", "detail"],
      method: "get",
      path: [CasesEndpoint.basePath, "detail", id],
      response: CasesEndpoint.schemas.detail.response,
    });
  }

  public async update(
    id: string,
    body: z.input<typeof CasesEndpoint.schemas.update.body>,
  ): ActionReturnType<z.infer<typeof CasesEndpoint.schemas.update.response>> {
    const bodyValidated = validateInput(CasesEndpoint.schemas.update.body, body);
    if (!bodyValidated.ok) return bodyValidated;
    return request({
      log: ["Cases", "update"],
      method: "put",
      path: [CasesEndpoint.basePath, id],
      json: bodyValidated.data,
      response: CasesEndpoint.schemas.update.response,
    });
  }

  public async review(
    body: z.input<typeof CasesEndpoint.schemas.review.body>,
  ): ActionReturnType<z.infer<typeof CasesEndpoint.schemas.review.response>> {
    const bodyValidated = validateInput(CasesEndpoint.schemas.review.body, body);
    if (!bodyValidated.ok) return bodyValidated;
    return request({
      log: ["Cases", "review"],
      method: "post",
      path: [CasesEndpoint.basePath, "review"],
      json: bodyValidated.data,
      response: CasesEndpoint.schemas.review.response,
      localErrorHandling: true,
      timeout: AI_REVIEW_TIMEOUT_MS,
    });
  }

  public delete(id: string | number): ActionReturnType<undefined> {
    return request({
      log: ["Cases", "delete"],
      method: "delete",
      path: [CasesEndpoint.basePath, id],
    });
  }
}

export { CasesEndpoint };
